<template>
    <Fieldset class="grid-cols-1 md:grid-cols-2 md:gap-6 gap-4">
        <template #legend>
            <h3 class="text-lg font-semibold">Podatki potnikov</h3>
        </template>

        <template v-for="section in guestSections" :key="section.key">
            <h4 class="text-base font-semibold md:col-span-2">{{ section.title }}</h4>
            <div v-for="(guest, listIndex) in section.items" :key="`${section.key}-${guest.index}`" class="border border-slate-200 rounded-xl p-4 md:col-span-2">
                <p class="text-sm font-medium text-slate-700 mb-4">{{ listIndex + 1 }}. {{ section.personLabel }}</p>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Field :error="getGuestFieldError(guest.index, 'first_name')" v-slot="{ hasError, errorId }">
                        <Label :for="`guest-first-name-${guest.index}`">{{ $t('First name') }}</Label>
                        <Input v-model="form.persons[guest.index].first_name" :id="`guest-first-name-${guest.index}`" autocomplete="given-name" :has-error="hasError" :error-id="errorId" />
                    </Field>

                    <Field :error="getGuestFieldError(guest.index, 'last_name')" v-slot="{ hasError, errorId }">
                        <Label :for="`guest-last-name-${guest.index}`">{{ $t('Last name') }}</Label>
                        <Input v-model="form.persons[guest.index].last_name" :id="`guest-last-name-${guest.index}`" autocomplete="family-name" :has-error="hasError" :error-id="errorId" />
                    </Field>

                    <Field :error="getGuestFieldError(guest.index, 'birth_date')" v-slot="{ hasError, errorId }">
                        <Label :for="`guest-date-of-birth-${guest.index}`">Datum rojstva</Label>
                        <Input v-model="form.persons[guest.index].birth_date" :id="`guest-date-of-birth-${guest.index}`" placeholder="dd.mm.llll" autocomplete="bday" :has-error="hasError" :error-id="errorId" />
                    </Field>
                </div>
            </div>
        </template>
    </Fieldset>
</template>

<script setup>
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const { form, guestCounts } = defineProps({
    form: {
        type: Object,
        required: true
    },
    guestCounts: {
        type: Object,
        required: true
    }
})

const buildPersons = (adultsCount, childrenCount, existingPersons = []) => {
    const adultDetails = existingPersons.filter((guest) => guest.type === 'adult')
    const childDetails = existingPersons.filter((guest) => guest.type === 'child')

    const createGuest = (type, existingGuest) => ({
        type,
        first_name: existingGuest?.first_name ?? '',
        last_name: existingGuest?.last_name ?? '',
        birth_date: existingGuest?.birth_date ?? ''
    })

    return [...Array.from({ length: adultsCount }, (_, index) => createGuest('adult', adultDetails[index])), ...Array.from({ length: childrenCount }, (_, index) => createGuest('child', childDetails[index]))]
}

watch(
    () => [guestCounts.adults, guestCounts.children],
    ([adultsCount, childrenCount]) => {
        form.persons = buildPersons(adultsCount, childrenCount, form.persons)
    },
    { immediate: true }
)

const adultGuestDetails = computed(() => {
    return form.persons.map((guest, index) => ({ ...guest, index })).filter((guest) => guest.type === 'adult')
})

const childGuestDetails = computed(() => {
    return form.persons.map((guest, index) => ({ ...guest, index })).filter((guest) => guest.type === 'child')
})

const guestSections = computed(() => {
    return [
        {
            key: 'adult',
            title: 'Adults',
            personLabel: 'Odrasla oseba',
            items: adultGuestDetails.value
        },
        {
            key: 'child',
            title: 'Children',
            personLabel: 'Otrok',
            items: childGuestDetails.value
        }
    ]
        .filter((section) => section.items.length)
        .map((section) => ({
            ...section,
            title: t(section.title)
        }))
})

const getGuestFieldError = (guestIndex, field) => {
    return form.errors[`persons.${guestIndex}.${field}`]
}
</script>
